/**
 * SqlDb (the interface apps/mobile/src/core/store.ts is written against) on top of sql.js,
 * SQLite compiled to WebAssembly, persisted to IndexedDB.
 *
 * Why this shape:
 *  - The mobile LocalStore runs unchanged on it, so the browser app has the same tables, the same
 *    queries and the same tested sync semantics as the phone app.
 *  - sql.js keeps the whole database in memory. After every write (and after every COMMIT) the
 *    database is exported and stored in IndexedDB *before* the write's promise resolves, so
 *    "saved on the phone" really means on disk.
 *  - sql.js has ONE connection. A statement issued by another async flow while a transaction is
 *    open would silently become part of that transaction (and be undone by its ROLLBACK). So every
 *    operation on the main handle takes a lock; the handle passed to transaction callbacks runs
 *    inside the lock. Callbacks must use that handle only (the mobile store does).
 *  - db.export() closes and reopens the sqlite connection, so it only ever runs under the lock.
 */
import type { Database, SqlJsStatic } from "sql.js";
import type { SqlDb, SqlRunResult, SqlValue } from "@core/sql";

/** Where the database file lives between page loads. */
export interface ByteStore {
  load(key: string): Promise<Uint8Array | null>;
  save(key: string, bytes: Uint8Array): Promise<void>;
}

export class MemoryByteStore implements ByteStore {
  readonly files = new Map<string, Uint8Array>();
  saves = 0;
  async load(key: string) {
    return this.files.get(key) ?? null;
  }
  async save(key: string, bytes: Uint8Array) {
    this.saves++;
    this.files.set(key, bytes.slice());
  }
}

class Mutex {
  private tail: Promise<unknown> = Promise.resolve();
  run<T>(fn: () => Promise<T> | T): Promise<T> {
    const p = this.tail.then(() => fn());
    this.tail = p.catch(() => undefined);
    return p;
  }
}

type Params = SqlValue[];

/** Statement helpers that assume the caller holds the lock. */
class Raw {
  constructor(private getDb: () => Database) {}

  exec(sql: string) {
    this.getDb().exec(sql);
  }

  run(sql: string, params: Params = []): SqlRunResult {
    const db = this.getDb();
    const stmt = db.prepare(sql);
    try {
      stmt.run(params as (string | number | null)[]);
    } finally {
      stmt.free();
    }
    const changes = db.getRowsModified();
    const r = db.exec("SELECT last_insert_rowid()");
    const lastInsertRowId = Number(r[0]?.values[0]?.[0] ?? 0);
    return { lastInsertRowId, changes };
  }

  all<T>(sql: string, params: Params = []): T[] {
    const stmt = this.getDb().prepare(sql);
    try {
      stmt.bind(params as (string | number | null)[]);
      const out: T[] = [];
      while (stmt.step()) out.push(stmt.getAsObject() as T);
      return out;
    } finally {
      stmt.free();
    }
  }

  first<T>(sql: string, params: Params = []): T | null {
    const rows = this.all<T>(sql, params);
    return rows.length ? rows[0] : null;
  }
}

export interface SqlJsDbOptions {
  SQL: SqlJsStatic;
  store: ByteStore;
  /** key of the database file in the ByteStore */
  key?: string;
}

export class SqlJsDb implements SqlDb {
  private readonly lock = new Mutex();
  private readonly raw: Raw;
  private spSeq = 0;
  /** true when the in-memory database has changes the ByteStore does not have yet */
  private unsaved = false;

  private constructor(private db: Database, private readonly store: ByteStore, private readonly key: string) {
    this.raw = new Raw(() => this.db);
  }

  static async open({ SQL, store, key = "npac-field.sqlite" }: SqlJsDbOptions): Promise<SqlJsDb> {
    const bytes = await store.load(key);
    const db = bytes && bytes.length ? new SQL.Database(bytes) : new SQL.Database();
    return new SqlJsDb(db, store, key);
  }

  /* ---------------- SqlDb ---------------- */

  exec(sql: string): Promise<void> {
    return this.lock.run(async () => {
      this.raw.exec(sql);
      this.unsaved = true;
      await this.persistLocked();
    });
  }

  run(sql: string, params?: SqlValue[]): Promise<SqlRunResult> {
    return this.lock.run(async () => {
      const r = this.raw.run(sql, params);
      this.unsaved = true;
      await this.persistLocked();
      return r;
    });
  }

  all<T>(sql: string, params?: SqlValue[]): Promise<T[]> {
    return this.lock.run(() => this.raw.all<T>(sql, params));
  }

  first<T>(sql: string, params?: SqlValue[]): Promise<T | null> {
    return this.lock.run(() => this.raw.first<T>(sql, params));
  }

  transaction(fn: (tx: SqlDb) => Promise<void>): Promise<void> {
    return this.lock.run(async () => {
      this.raw.exec("BEGIN");
      try {
        await fn(this.txHandle());
        this.raw.exec("COMMIT");
      } catch (e) {
        try {
          this.raw.exec("ROLLBACK");
        } catch {
          /* already rolled back by sqlite */
        }
        throw e;
      }
      this.unsaved = true;
      await this.persistLocked();
    });
  }

  /** Write the current database to the ByteStore now (e.g. on pagehide). */
  flush(): Promise<void> {
    return this.lock.run(() => this.persistLocked());
  }

  /** Size of the exported database in bytes (for diagnostics). */
  size(): Promise<number> {
    return this.lock.run(() => this.db.export().length);
  }

  /* ---------------- internals ---------------- */

  /** Handle for transaction callbacks: runs directly (the lock is already held). */
  private txHandle(): SqlDb {
    const raw = this.raw;
    const self = this;
    const handle: SqlDb = {
      async exec(sql) {
        raw.exec(sql);
      },
      async run(sql, params) {
        return raw.run(sql, params);
      },
      async all<T>(sql: string, params?: SqlValue[]) {
        return raw.all<T>(sql, params);
      },
      async first<T>(sql: string, params?: SqlValue[]) {
        return raw.first<T>(sql, params);
      },
      async transaction(inner) {
        // nested transaction → savepoint
        const sp = `sp${++self.spSeq}`;
        raw.exec(`SAVEPOINT ${sp}`);
        try {
          await inner(handle);
          raw.exec(`RELEASE ${sp}`);
        } catch (e) {
          raw.exec(`ROLLBACK TO ${sp}`);
          raw.exec(`RELEASE ${sp}`);
          throw e;
        }
      },
    };
    return handle;
  }

  private async persistLocked(): Promise<void> {
    if (!this.unsaved) return;
    const bytes = this.db.export(); // closes + reopens the connection; only safe under the lock
    this.unsaved = false;
    try {
      await this.store.save(this.key, bytes);
    } catch (e) {
      this.unsaved = true; // retried with the next write or flush()
      throw new StorageError(e instanceof Error ? e.message : String(e));
    }
  }
}

/** The phone's storage refused the write (quota, private mode, …). The data is still in memory. */
export class StorageError extends Error {
  constructor(message: string) {
    super(`Could not save to phone storage: ${message}`);
    this.name = "StorageError";
  }
}
